/**
 * RiskPropagationMap
 *
 * Tree/graph visualization showing how a risk event cascades through the
 * system: from the originating risk event, through affected services, to
 * impacted controls and ultimately the compliance frameworks they belong to.
 *
 * Layout: top-down tree rendered with CSS flexbox (not force-directed).
 *   Level 0: Risk event node (red diamond via CSS rotate)
 *   Level 1: Affected services (amber rounded rectangles)
 *   Level 2: Affected controls (hexagon-like badges, blue)
 *   Level 3: Affected frameworks (green rounded rectangles)
 *
 * Lines connecting levels are rendered with pure SVG.
 * Uses Fluent UI v9 makeStyles, tokens, typography, Badge, and Card.
 *
 * No external charting or graph libraries.
 */
import React, { useMemo } from 'react';
import {
  makeStyles,
  mergeClasses,
  tokens,
  Card,
  Badge,
  Body1,
  Body2,
  Caption1,
  shorthands,
} from '@fluentui/react-components';
import { semanticColors } from '../../theme/solution-theme';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface RiskPropagationMapProps {
  riskEvent?: {
    id: string;
    type: string; // "secret_detected", "dependency_vuln", "policy_violation"
    description: string;
    severity: 'critical' | 'high' | 'medium' | 'low';
    affectedServices: Array<{ name: string; criticality: 'high' | 'medium' | 'low' }>;
    affectedControls: Array<{
      controlId: string;
      framework: string;
      confidenceDrop?: number;
    }>;
    affectedFrameworks: string[];
    blastRadius: {
      servicesCount: number;
      controlsCount: number;
      frameworksCount: number;
    };
  };
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const RISK_RED = semanticColors.error;
// AFFECTED_AMBER (#F59E0B) and CONTROL_BLUE (#0078D4) are not in brand-config.json's
// palette — no token represents them, so they are left as raw literals.
const AFFECTED_AMBER = '#F59E0B';
const CONTROL_BLUE = '#0078D4';
const FRAMEWORK_GREEN = semanticColors.success;

// #DC2626 (dark red) and #3B82F6 (blue) are not in brand-config.json's palette —
// no token represents them, so they are left as raw literals.
const SEVERITY_COLORS: Record<string, string> = {
  critical: '#DC2626',
  high: semanticColors.error,
  medium: '#F59E0B',
  low: '#3B82F6',
};

const CRITICALITY_COLORS: Record<string, string> = {
  high: '#DC2626',
  medium: '#F59E0B',
  low: '#3B82F6',
};

const TYPE_LABELS: Record<string, string> = {
  secret_detected: 'Secret Detected',
  dependency_vuln: 'Dependency Vulnerability',
  policy_violation: 'Policy Violation',
};

const DEFAULT_RISK_EVENT: NonNullable<RiskPropagationMapProps['riskEvent']> = {
  id: 'RSK-0042',
  type: 'secret_detected',
  description: 'API key committed to payments-api repository',
  severity: 'critical',
  affectedServices: [
    { name: 'payments-api', criticality: 'high' },
    { name: 'billing-service', criticality: 'medium' },
    { name: 'auth-gateway', criticality: 'high' },
  ],
  affectedControls: [
    { controlId: 'CC6.1', framework: 'SOC2', confidenceDrop: 18 },
    { controlId: 'A12.4', framework: 'ISO27001', confidenceDrop: 12 },
  ],
  affectedFrameworks: ['SOC2', 'ISO27001'],
  blastRadius: {
    servicesCount: 3,
    controlsCount: 2,
    frameworksCount: 2,
  },
};

/* ------------------------------------------------------------------ */
/*  Keyframes (injected into document head)                            */
/* ------------------------------------------------------------------ */

const KEYFRAMES_ID = 'risk-propagation-map-keyframes';

const keyframesCSS = `
@keyframes rpm-pulse-risk {
  0%, 100% { box-shadow: 0 0 0 0 ${RISK_RED}80; }
  50% { box-shadow: 0 0 0 8px ${RISK_RED}00; }
}

@keyframes rpm-dash-flow {
  0% { stroke-dashoffset: 12; }
  100% { stroke-dashoffset: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .rpm-pulse-risk { animation: none !important; }
  .rpm-dash-flow { animation: none !important; }
}
`;

/* ------------------------------------------------------------------ */
/*  Styles                                                             */
/* ------------------------------------------------------------------ */

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },

  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM,
  },

  blastRadiusSummary: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },

  blastDot: {
    width: '6px',
    height: '6px',
    ...shorthands.borderRadius('50%'),
    flexShrink: 0,
  },

  blastSegment: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },

  blastSeparator: {
    color: tokens.colorNeutralStroke2,
    userSelect: 'none' as const,
  },

  /* --- Tree layout --- */

  tree: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingHorizontalNone, // connectors handle vertical spacing
    ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalM),
    overflowX: 'auto',
  },

  level: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalL,
    flexWrap: 'wrap',
  },

  /* --- Connector SVG between levels --- */

  connectorRow: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    flexShrink: 0,
  },

  /* --- Risk event node (diamond) --- */

  diamondWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalS,
  },

  diamond: {
    width: '56px',
    height: '56px',
    transform: 'rotate(45deg)',
    ...shorthands.borderRadius('6px'),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },

  diamondInner: {
    transform: 'rotate(-45deg)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: tokens.fontSizeBase500,
    fontWeight: 700 as const,
    color: tokens.colorNeutralForegroundStaticInverted,
    userSelect: 'none' as const,
  },

  diamondLabel: {
    textAlign: 'center' as const,
    maxWidth: '200px',
  },

  /* --- Service node --- */

  serviceNode: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalXS,
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    backgroundColor: tokens.colorNeutralBackground1,
    minWidth: '130px',
    maxWidth: '170px',
    textAlign: 'center' as const,
    transitionProperty: 'box-shadow, transform',
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      boxShadow: tokens.shadow8,
      transform: 'translateY(-2px)',
    },
  },

  /* --- Control node (hexagon-like) --- */

  controlNode: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalXS,
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL),
    backgroundColor: tokens.colorNeutralBackground1,
    minWidth: '140px',
    maxWidth: '180px',
    textAlign: 'center' as const,
    clipPath: 'polygon(10% 0%, 90% 0%, 100% 50%, 90% 100%, 10% 100%, 0% 50%)',
    transitionProperty: 'box-shadow, transform',
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      transform: 'translateY(-2px)',
    },
  },

  /* --- Framework node --- */

  frameworkNode: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalXS,
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL),
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    backgroundColor: tokens.colorNeutralBackground1,
    minWidth: '120px',
    maxWidth: '160px',
    textAlign: 'center' as const,
    transitionProperty: 'box-shadow, transform',
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      boxShadow: tokens.shadow8,
      transform: 'translateY(-2px)',
    },
  },

  /* --- Level label --- */

  levelLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase100,
    fontWeight: 600 as const,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    ...shorthands.padding(tokens.spacingVerticalXS, '0'),
    alignSelf: 'center',
  },

  /* --- Mono metric text --- */

  monoMetric: {
    fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
    fontVariantNumeric: 'tabular-nums',
  },

  confidenceDrop: {
    fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
    fontVariantNumeric: 'tabular-nums',
    color: RISK_RED,
    fontWeight: 600 as const,
  },
});

/* ------------------------------------------------------------------ */
/*  SVG connector between tree levels                                  */
/* ------------------------------------------------------------------ */

interface LevelConnectorProps {
  parentCount: number;
  childCount: number;
  color: string;
}

/**
 * Renders SVG lines fanning from each parent to every child.
 * Parents are distributed evenly across the top; children across the bottom.
 * The SVG stretches to fill the available width so that lines stay aligned
 * with the flex-distributed node rows above and below.
 */
const LevelConnector: React.FC<LevelConnectorProps> = ({
  parentCount,
  childCount,
  color,
}) => {
  if (parentCount === 0 || childCount === 0) return null;

  const svgHeight = 40;

  // We use a viewBox-less SVG with percentage-based x positions.
  // Since SVG doesn't natively support % on <line>, we compute positions
  // relative to a fixed internal width and let viewBox scale.
  const internalWidth = 600;

  const parentXPositions = Array.from({ length: parentCount }, (_, i) =>
    parentCount === 1 ? internalWidth / 2 : (internalWidth * (i + 0.5)) / parentCount,
  );

  const childXPositions = Array.from({ length: childCount }, (_, i) =>
    childCount === 1 ? internalWidth / 2 : (internalWidth * (i + 0.5)) / childCount,
  );

  return (
    <div className="rpm-connector-row" style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
      <svg
        width="100%"
        height={svgHeight}
        viewBox={`0 0 ${internalWidth} ${svgHeight}`}
        preserveAspectRatio="none"
        style={{ maxWidth: `${Math.max(parentCount, childCount) * 190}px`, display: 'block' }}
        aria-hidden="true"
      >
        {parentXPositions.map((px, pi) =>
          childXPositions.map((cx, ci) => (
            <line
              key={`${pi}-${ci}`}
              x1={px}
              y1={0}
              x2={cx}
              y2={svgHeight}
              stroke={color}
              strokeWidth="2"
              strokeDasharray="6 4"
              strokeOpacity={0.6}
              className="rpm-dash-flow"
              style={{ animation: 'rpm-dash-flow 0.8s linear infinite' }}
            />
          )),
        )}
      </svg>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export const RiskPropagationMap: React.FC<RiskPropagationMapProps> = ({ riskEvent }) => {
  const styles = useStyles();
  const event = useMemo(() => riskEvent ?? DEFAULT_RISK_EVENT, [riskEvent]);

  // Inject keyframes once
  React.useEffect(() => {
    if (typeof document === 'undefined') return;
    if (document.getElementById(KEYFRAMES_ID)) return;
    const style = document.createElement('style');
    style.id = KEYFRAMES_ID;
    style.textContent = keyframesCSS;
    document.head.appendChild(style);
    return () => {
      const el = document.getElementById(KEYFRAMES_ID);
      if (el) el.remove();
    };
  }, []);

  const severityColor = SEVERITY_COLORS[event.severity] ?? RISK_RED;
  const typeLabel = TYPE_LABELS[event.type] ?? event.type;

  return (
    <div className={styles.root} role="region" aria-label="Risk Propagation Map">
      {/* Header: title + blast radius summary */}
      <div className={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
          <Body1 style={{ fontWeight: 600 }}>Risk Propagation</Body1>
          <Badge
            appearance="filled"
            color="danger"
            style={{ backgroundColor: severityColor }}
          >
            {event.severity.toUpperCase()}
          </Badge>
        </div>

        <div className={styles.blastRadiusSummary} aria-label="Blast radius summary">
          <div className={styles.blastSegment}>
            <span className={styles.blastDot} style={{ backgroundColor: AFFECTED_AMBER }} />
            <span>{event.blastRadius.servicesCount} services</span>
          </div>
          <span className={styles.blastSeparator}>|</span>
          <div className={styles.blastSegment}>
            <span className={styles.blastDot} style={{ backgroundColor: CONTROL_BLUE }} />
            <span>{event.blastRadius.controlsCount} controls</span>
          </div>
          <span className={styles.blastSeparator}>|</span>
          <div className={styles.blastSegment}>
            <span className={styles.blastDot} style={{ backgroundColor: FRAMEWORK_GREEN }} />
            <span>{event.blastRadius.frameworksCount} frameworks affected</span>
          </div>
        </div>
      </div>

      {/* Tree visualization */}
      <Card>
        <div className={styles.tree}>
          {/* Level 0 — Risk event (diamond) */}
          <Caption1 className={styles.levelLabel}>Risk Event</Caption1>
          <div className={styles.level}>
            <div className={styles.diamondWrapper}>
              <div
                className={mergeClasses(styles.diamond, 'rpm-pulse-risk')}
                style={{
                  backgroundColor: severityColor,
                  boxShadow: `0 0 0 0 ${severityColor}80`,
                  animation: 'rpm-pulse-risk 2s ease-in-out infinite',
                }}
                role="img"
                aria-label={`Risk event: ${typeLabel} - ${event.description}`}
              >
                <span className={styles.diamondInner}>!</span>
              </div>
              <Body2 style={{ fontWeight: 600, color: severityColor }}>{typeLabel}</Body2>
              <Caption1 className={styles.diamondLabel}>{event.description}</Caption1>
              <Caption1 className={styles.monoMetric} style={{ color: tokens.colorNeutralForeground3 }}>
                {event.id}
              </Caption1>
            </div>
          </div>

          {/* Connector: Risk -> Services */}
          <LevelConnector
            parentCount={1}
            childCount={event.affectedServices.length}
            color={RISK_RED}
          />

          {/* Level 1 — Affected services */}
          <Caption1 className={styles.levelLabel}>Affected Services</Caption1>
          <div className={styles.level}>
            {event.affectedServices.map((svc) => {
              const critColor = CRITICALITY_COLORS[svc.criticality] ?? AFFECTED_AMBER;
              return (
                <div
                  key={svc.name}
                  className={styles.serviceNode}
                  style={{
                    border: `2px solid ${AFFECTED_AMBER}`,
                    boxShadow: `0 0 0 1px ${AFFECTED_AMBER}22`,
                  }}
                  role="group"
                  aria-label={`Affected service: ${svc.name}, criticality ${svc.criticality}`}
                >
                  <Body2 style={{ fontWeight: 600, color: tokens.colorNeutralForeground1 }}>
                    {svc.name}
                  </Body2>
                  <Badge
                    size="small"
                    appearance="tint"
                    style={{
                      backgroundColor: `${critColor}18`,
                      color: critColor,
                      border: `1px solid ${critColor}44`,
                    }}
                  >
                    {svc.criticality}
                  </Badge>
                </div>
              );
            })}
          </div>

          {/* Connector: Services -> Controls */}
          <LevelConnector
            parentCount={event.affectedServices.length}
            childCount={event.affectedControls.length}
            color={AFFECTED_AMBER}
          />

          {/* Level 2 — Affected controls (hexagon badges) */}
          <Caption1 className={styles.levelLabel}>Impacted Controls</Caption1>
          <div className={styles.level}>
            {event.affectedControls.map((ctrl) => (
              <div
                key={`${ctrl.framework}-${ctrl.controlId}`}
                className={styles.controlNode}
                style={{
                  backgroundColor: `${CONTROL_BLUE}0D`,
                  border: `2px solid ${CONTROL_BLUE}44`,
                }}
                role="group"
                aria-label={`Control ${ctrl.controlId} (${ctrl.framework})${ctrl.confidenceDrop ? `, confidence drop ${ctrl.confidenceDrop}%` : ''}`}
              >
                <Body2 style={{ fontWeight: 700, color: CONTROL_BLUE }}>
                  {ctrl.controlId}
                </Body2>
                <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                  {ctrl.framework}
                </Caption1>
                {ctrl.confidenceDrop !== undefined && (
                  <span className={styles.confidenceDrop}>
                    -{ctrl.confidenceDrop}%
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* Connector: Controls -> Frameworks */}
          <LevelConnector
            parentCount={event.affectedControls.length}
            childCount={event.affectedFrameworks.length}
            color={CONTROL_BLUE}
          />

          {/* Level 3 — Affected frameworks */}
          <Caption1 className={styles.levelLabel}>Frameworks at Risk</Caption1>
          <div className={styles.level}>
            {event.affectedFrameworks.map((fw) => (
              <div
                key={fw}
                className={styles.frameworkNode}
                style={{
                  border: `2px solid ${FRAMEWORK_GREEN}`,
                  boxShadow: `0 0 0 1px ${FRAMEWORK_GREEN}22`,
                }}
                role="group"
                aria-label={`Framework at risk: ${fw}`}
              >
                <Body2 style={{ fontWeight: 600, color: FRAMEWORK_GREEN }}>
                  {fw}
                </Body2>
                <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                  Compliance impact
                </Caption1>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
};
